/*
 * CartPage - this page is used to handle empty cart page and to show items in the cart
*/

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import * as actions from '@redux-actions/';

import Cart from '@components/Cart';
import Shipping from '@components/Shipping';
import PageTitle from '@components/PageTitle';
import FaqList from '@components/FaqList';
import CartLoader from '@components/CartLoader';
import useFormattedOrderItems from '@hooks/useFormattedOrderItems';
import SessionModal from '@components/SessionModal';
import GenericErrorModal from '@components/GenericErrorModal';
import useTealiumData from '@hooks/useTealiumData';
import useMonetate from '@hooks/useMonetate';
import useWunderkind from '@hooks/useWunderkind';

import { getLabelFallback, animateTextBanner, getDiscountNames, getDiscoutCodes } from '@utils/utils';
import useScrollTop from '@hooks/useScrollTop';

const CartPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    //get query string from the url
    const { search } = useLocation();

    //get variables from redux store
    const cart = useSelector(state => state.cart.cart);
    const isLoading = useSelector(state => state.cart.isLoading);
    const isLoaded = useSelector(state => state.cart.isLoaded);
    const config = useSelector(state => state.cart.config);
    const isMobile = useSelector(state => state.cart.isMobile);
    const faq = useSelector(state => state.cart.faq);
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const showSessionModal = useSelector(state => state.cart.showSessionModal);
    const showGenericErrorModal = useSelector(state => state.cart.showGenericErrorModal);
    const disableNavigation = useSelector(state => state.cart.disableNavigation);

    //define state variables for the page
    const [hasSmartGlasses, setHasSmartGlasses] = useState(false);
    const [smartGlassesError, setSmartGlassesError] = useState('');
    const [allProductsAreSpeedy, setAllProductsAreSpeedy] = useState(false);

    const [hasSafetyGlasses, setHasSafetyGlasses] = useState(false);
    const [frpushed, setfrpushed] = useState(false);

    const speedyOrderDetails = useSelector(state => state.cart.speedyOrderDetails);
    
    //checkoutClick - function called when the user clicks on 'Secure checkout' button
    const checkoutClick = () => {
        //control if there are some errors relative to smart glasses
        if(smartGlassesError.length > 0) {
            animateTextBanner();
        }

        if(smartGlassesError.length === 0) {
            if(typeof $TB != 'undefined' && !!$TB.hooks) {
                $TB.hooks.fireEvent("pageChange", null, {"sendBeacon": true});
            }
            dispatch(actions.startLoading());

            //navigate to shipping page
            navigate(config.pagesUrl.shipping + search);
        }
    }

    /*
        this hooks at first render:
        - call the function for hide the custom footer
        - call the function to get the FAQ
    */
    useEffect(() => {   
        config.callFromComponentToGl.hideHeaderFooter(false);
        dispatch(actions.setFaq(config.hostname, config.mockRequests));
        dispatch(actions.setUpsellDetails(config.hostname));  
    }, []);

    //hook for retrieve data to show
    useFormattedOrderItems(cart); 

    //hook for set analytics data
    useTealiumData('CartPage', cart);

	//hook for set monetate data
	useMonetate('Cart', cart);
    
    //hook for set Wunderkind data
	useWunderkind('Cart', cart);
    

    //hook to scroll top the page
    useScrollTop();

    //this hooks, control every time the cart is changed if there are safety glasses
    useEffect(() => {
        if(!!formattedOrderItems) {
            const numberOfSafety = formattedOrderItems.filter(item => !!item.moCo.prod_hierarchy_2 && item.moCo.prod_hierarchy_2.toLowerCase() === 'safety eyeglasses').length;
            if(numberOfSafety === 0) {
                setHasSafetyGlasses(false);
            } else {
                setHasSafetyGlasses(true);
            }
        }
    }, [formattedOrderItems]);

    //this hooks, control every time the cart is changed if there are some errors with smart glasses
    useEffect(() => {
        if(!!formattedOrderItems) {
            const numberOfSmart = formattedOrderItems.filter(item => !!item.moCo.prod_hierarchy_1 && item.moCo.prod_hierarchy_1.toLowerCase() === 'electronics').length;
            const numberOfSpeedy = speedyOrderDetails?.glassesDetails?.filter(item => item?.speedyEligible).length;
            
            if(numberOfSmart === 0) {
                setHasSmartGlasses(false);
                setSmartGlassesError('');
            } else {
                setHasSmartGlasses(true);
                
                if(formattedOrderItems.length > 1 && numberOfSmart !== formattedOrderItems.length) {
                    setSmartGlassesError('TOO_MANY_GLASSES');
                    window?.tealium_data2track.push({
                        'id': 'Event',
                        'Events_Error': '1',
                        'Error_Source': 'User',
                        'Error_Code': 'Mixed Cart',
                        'Error_Details': 'Mixed cart – Incompatible products in cart',
                        'Error_Message': getLabelFallback('TOO_MANY_GLASSES_CLAIM'),
                    });
                } else {
                    if(numberOfSmart > 1) {
                        setSmartGlassesError('SMART_GLASSES');
                    } else {
                        setSmartGlassesError('');
                    }
                }
            }

            if(numberOfSpeedy > 0){
                setAllProductsAreSpeedy(true);
            }else{
                setAllProductsAreSpeedy(false);
            }
        }
        
    }, [formattedOrderItems]);

    useEffect(() => {
        if(!!isLoaded && !frpushed  && typeof $TB != 'undefined' && !!$TB.hooks && $TB.hooks.events.length > 0) {
            $TB.hooks.fireEvent("pageChange", null, {"sendBeacon": true});
            setfrpushed(true);
        }
    }, [isLoaded]);

    return (
        <section className={`cart-page-container ${disableNavigation > 0 ? 'disable-navigation' : ''}`}>
            {!!cart && !!cart.orderItem && cart.orderItem.length > 0 &&
                <>
                    <div className="cart-page">
                        <div className="cart-page-left">
                            <Cart checkoutClick={checkoutClick} smartGlassesError={smartGlassesError}>
                                {!isMobile && !!faq &&
                                    <div className="cart-page-left cart-page-faq desktop">
                                        <h2 className="cart-page-faq-title">{getLabelFallback('FAQ_TITLE')}</h2>
                                        <label className="cart-page-faq-claim">{getLabelFallback('FAQ_CLAIM')}</label>
                                        <FaqList faq={faq} />
                                    </div>
                                }
                            </Cart>
                        </div>
                        <div className="cart-page-right">
                            <Shipping checkoutClick={checkoutClick} hasSmartGlasses={hasSmartGlasses} smartGlassesError={smartGlassesError} hasSafetyGlasses={hasSafetyGlasses} seeSpeedyCheckbox={allProductsAreSpeedy}/>
                        </div>
                    </div>
                    {
                        isMobile && !!faq &&
                            <div className="cart-page">
                                <div className="cart-page-left cart-page-faq">
                                    <h2 className="cart-page-faq-title">{getLabelFallback('FAQ_TITLE')}</h2>
                                    <label className="cart-page-faq-claim">{getLabelFallback('FAQ_CLAIM')}</label>
                                        <FaqList faq={faq} />
                                </div>
                               
                            </div>
                    }
                </>
            }
            {(!cart || (!!cart && !cart.orderItem)) && !isLoading && !isLoaded &&
                <div className="cart-page-empty">
                    <PageTitle label={ getLabelFallback('CART_PAGE_TITLE') }/>
                    <label className="cart-page-empty-label">{ getLabelFallback('EMPTY_PAGE_CLAIM_1') }</label>
                    <label className="cart-page-empty-label">{ getLabelFallback('EMPTY_PAGE_CLAIM_2') }</label>
                    {(config.linkEyeGlasses || config.linkSunGlasses) && <div className="cart-page-empty-buttons">
                        {config.linkEyeGlasses &&
                            <a href={config.linkEyeGlasses} aria-label={getLabelFallback('SHOP_EYEGLASSES_LABEL')} className="violet-button">{ getLabelFallback('SHOP_EYEGLASSES_LABEL') }</a>
                        }
                        {config.linkSunGlasses &&
                            <a href={config.linkSunGlasses} aria-label={getLabelFallback('SHOP_SUNGLASSES_LABEL')} className="violet-button">{ getLabelFallback('SHOP_SUNGLASSES_LABEL') }</a>
                        }
                    </div>}
                </div>
            }
            {isLoading &&
                <CartLoader />
            }
            {
                showSessionModal && <SessionModal />
            }
            {
                showGenericErrorModal && <GenericErrorModal />
            }
        </section>
    );
}

export default CartPage;